import { useEffect, useState } from "react";
import { useParams, Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import api, { fmtNZD, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OutOfTownProjectWeather } from "@/components/WeatherChipRow";
import DeleteProjectDialog from "@/components/DeleteProjectDialog";
import EditJobDialog from "@/components/EditJobDialog";
import CompleteJobDialog from "@/components/CompleteJobDialog";
import JobChecklist from "@/pages/project/JobChecklist";
import { QuickBadge } from "@/components/QuickBadge";
import EstimatePanel from "@/pages/project/EstimatePanel";

const TAB = ({ to, label, testid, end = false }) => (
  <NavLink to={to} end={end} data-testid={testid}
    className={({ isActive }) => `px-3 py-2 text-sm rounded whitespace-nowrap ${isActive ? "bg-silver text-black" : "text-white hover:text-silver"}`}>
    {label}
  </NavLink>
);

export default function ProjectWorkspace() {
  const { pid } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [summary, setSummary] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [ck, setCk] = useState(0);
  const nav = useNavigate();
  const isMgmt = user?.role !== "Worker";

  async function load() {
    try {
      const { data } = await api.get(`/projects/${pid}`);
      setProject(data);
      setCk(k => k + 1);
      if (isMgmt) {
        const s = await api.get(`/projects/${pid}/financials-summary`).catch(() => null);
        setSummary(s?.data);
      }
    } catch { nav("/projects"); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  if (!project) return <div className="opacity-60">Loading…</div>;
  const closed = project.status === "Complete" || project.status === "Archived";
  async function reopen() {
    if (!window.confirm("Reopen this project? Workers will be able to log hours again.")) return;
    try { await api.post(`/projects/${pid}/reopen`); load(); } catch (e) { alert(e?.response?.data?.detail || "Couldn't reopen"); }
  }

  return (
    <div className="space-y-3" data-testid={`project-ws-${pid}`}>
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div>
          <Link to="/projects" className="hub-link text-sm">← Projects</Link>
          <h1 className="text-2xl mt-1">{project.name} <span className="opacity-50 text-base">#{project.reference || "—"}</span><QuickBadge p={project} testid="ws-quick-badge"/></h1>
          <div className="opacity-70 text-sm">{project.address}</div>
          {isMgmt && project.quote_accepted_at && <div className="text-green-400 text-xs" data-testid="ws-quote-accepted">Quote accepted {fmtDate(project.quote_accepted_at)}</div>}
          {isMgmt && summary && summary.pricing === "estimate" && (
            <div className="text-sky-300 text-sm mt-1" data-testid="project-estimate-header">Estimated <b data-testid="hdr-estimated">{summary.estimate_label}</b> <span className="opacity-60 text-xs">(estimate, not a contract)</span></div>
          )}
          {isMgmt && project.estimate_start_figure > 0 && !project.quote_accepted_at && <div className="text-sky-300 text-xs" data-testid="ws-est-start">Starting figure from estimate: {fmtNZD(project.estimate_start_figure)} (build and accept the quote to set the contract)</div>}
          {isMgmt && summary && summary.pricing !== "estimate" && (
            <div className="text-silver text-sm mt-1" data-testid="project-contract-header">
              Original <b data-testid="hdr-original">{fmtNZD(summary.original_contract)}</b> + Approved variations <b data-testid="hdr-approved">{fmtNZD(summary.approved_variations)}</b> = Current <b data-testid="hdr-current">{fmtNZD(summary.current_contract_total)}</b>
            </div>
          )}
        </div>
        {isMgmt && (
          <div className="flex gap-2 flex-wrap">
            {closed ? (
              <button data-testid="btn-reopen-job" onClick={reopen} className="btn-silver">Reopen</button>
            ) : (<>
              <button data-testid="btn-edit-job" onClick={() => setEditing(true)} className="btn-ghost">Edit project details</button>
              <button data-testid="btn-complete-job" onClick={() => setCompleting(true)} className="btn-ghost">Complete project</button>
            </>)}
            {project.status === "Complete" && <button data-testid="btn-edit-job" onClick={() => setEditing(true)} className="btn-ghost">Edit project details</button>}
            {user?.role === "Admin" && <button data-testid="btn-delete-project" onClick={() => setConfirmDelete(true)} className="btn-ghost text-red-400">Delete project</button>}
          </div>
        )}
      </div>

      {closed && (
        <div className="border border-neutral-700 bg-neutral-900 rounded p-2 text-sm" data-testid="job-closed-banner">
          <b>{project.status}</b>{project.completed_at ? ` · completed ${fmtDate(project.completed_at)}` : ""}{project.archived_at ? ` · archived ${fmtDate(project.archived_at)}` : ""}.
          {project.status === "Archived" ? " Read-only." : ""} Workers can't log hours to this project.
        </div>
      )}
      {completing && <CompleteJobDialog project={project} onClose={() => setCompleting(false)} onDone={() => { setCompleting(false); load(); }} />}

      {editing && <EditJobDialog project={project} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}

      {confirmDelete && (
        <DeleteProjectDialog project={project}
          onClose={() => setConfirmDelete(false)}
          onDeleted={() => { setConfirmDelete(false); nav("/projects"); }}/>
      )}

      <div data-testid="project-weather-slot">
        <OutOfTownProjectWeather projectId={project.id} testid="project-weather-chip-row"/>
      </div>

      {isMgmt && <JobChecklist key={`${pid}-${ck}`} projectId={pid}/>}
      {isMgmt && project.pricing === "estimate" && <EstimatePanel key={`est-${ck}`} project={project} reload={load}/>}

      <div className="flex flex-wrap gap-1 border-b border-neutral-800 pb-2 overflow-x-auto">
        {isMgmt && project.job_type === "quick" && <TAB to={`/projects/${pid}/chargeup`} label="Charge-up" testid="ptab-chargeup"/>}
        {isMgmt && project.job_type !== "quick" && <TAB to={`/projects/${pid}/estimate`} label="Plan and quote" testid="ptab-plan"/>}
        {isMgmt && <TAB to={`/projects/${pid}/subcontractors`} label="Subcontractors" testid="ptab-subs"/>}
        {isMgmt && <TAB to={`/projects/${pid}/materials`} label="Materials" testid="ptab-materials"/>}
        {isMgmt && <TAB to={`/projects/${pid}/labour`} label="Labour" testid="ptab-labour"/>}
        <TAB to={`/projects/${pid}`} end label="Stages and tasks" testid="ptab-stages"/>
        {isMgmt && <TAB to={`/projects/${pid}/bills`} label="Bills" testid="ptab-bills"/>}
        <TAB to={`/projects/${pid}/hours`} label="Hours" testid="ptab-hours"/>
        {isMgmt && <TAB to={`/projects/${pid}/programme`} label="Programme" testid="ptab-programme"/>}
        {isMgmt && <TAB to={`/projects/${pid}/report`} label="Report" testid="ptab-report"/>}
        <TAB to={`/projects/${pid}/more`} label="More" testid="ptab-more"/>
      </div>

      <fieldset disabled={project.status === "Archived"} className="min-w-0" data-testid="project-tab-body">
        <Outlet context={{ project, reload: load }} />
      </fieldset>
    </div>
  );
}
